/**
 * Agency detail (/agencies/:id) — V0.3 Tier 1.
 *
 * Three tabs:
 *   Overview  — contract + billing snapshot
 *   Roster    — guards on this agency's roster (links to guard detail)
 *   Invoices  — monthly invoices: generate / mark-paid / dispute / edit-draft
 *
 * Contracts (locked with the backend agent):
 *   GET    /v1/agencies/:id                       → SecurityAgency & {_count}
 *   GET    /v1/guards?agency_id=:id               → roster
 *   GET    /v1/agencies/:id/invoices              → list
 *   POST   /v1/agencies/:id/invoices              → generate (or override)
 *   PATCH  /v1/agency-invoices/:id                → edit draft
 *   POST   /v1/agency-invoices/:id/mark-paid      → close
 *   POST   /v1/agency-invoices/:id/dispute        → flag
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  CalendarDays,
  CheckCircle2,
  Edit3,
  Mail,
  Phone,
  Plus,
  Power,
  Receipt,
  RotateCcw,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, del, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Badge } from '@/components/ui/badge';
import { TabsPills } from '@/components/ui/tabs-pills';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { formatDate, formatDateTime } from '@/lib/format';
import type {
  AgencyInvoice,
  AgencyInvoiceStatus,
  Guard,
  SecurityAgency
} from './_agency-types';

// ───── label maps ─────

const STATUS_LABEL: Record<AgencyInvoiceStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  paid: 'Paid',
  disputed: 'Disputed'
};

const STATUS_VARIANT: Record<AgencyInvoiceStatus, 'muted' | 'info' | 'ok' | 'alert'> = {
  draft: 'muted',
  sent: 'info',
  paid: 'ok',
  disputed: 'alert'
};

// Helpers
function formatInr(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `₹${n.toLocaleString('en-IN')}`;
}

function monthLabel(m: number, y: number): string {
  const d = new Date(y, m - 1, 1);
  return new Intl.DateTimeFormat('en-IN', { month: 'short', year: 'numeric' }).format(d);
}

// ───── forms ─────

const InvoiceCreateSchema = z.object({
  period_month: z.coerce.number().int().min(1).max(12),
  period_year: z.coerce.number().int().min(2020).max(2100),
  active_guards: z.coerce.number().int().min(0).max(10_000),
  amount_inr: z.coerce.number().int().min(0).max(1_000_000_000),
  notes: z.string().trim().max(1000).optional().or(z.literal(''))
});
type InvoiceCreateValues = z.infer<typeof InvoiceCreateSchema>;

const InvoiceEditSchema = z.object({
  active_guards: z.coerce.number().int().min(0).max(10_000),
  amount_inr: z.coerce.number().int().min(0).max(1_000_000_000),
  notes: z.string().trim().max(1000).optional().or(z.literal(''))
});
type InvoiceEditValues = z.infer<typeof InvoiceEditSchema>;

const MarkPaidSchema = z.object({
  payment_ref: z.string().trim().min(1, 'Payment reference required').max(120)
});
type MarkPaidValues = z.infer<typeof MarkPaidSchema>;

const DisputeSchema = z.object({
  notes: z.string().trim().min(3, 'Reason required').max(1000)
});
type DisputeValues = z.infer<typeof DisputeSchema>;

// ───── page ─────

type Tab = 'overview' | 'roster' | 'invoices';

export function AgencyDetailPage(): JSX.Element {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('overview');
  const [deactivateTarget, setDeactivateTarget] = useState<SecurityAgency | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SecurityAgency | null>(null);

  // 1. Agency core
  const agencyQ = useQuery({
    queryKey: ['agency-detail', id],
    queryFn: () => get<SecurityAgency>(`/agencies/${id}`),
    enabled: Boolean(id)
  });
  const agency = agencyQ.data;

  // 2. Roster (paged client-side; usually small)
  const rosterQ = useQuery({
    queryKey: ['agency-roster', id],
    queryFn: () =>
      get<{ items: Guard[]; next_cursor?: string | null }>('/guards', {
        params: { agency_id: id, limit: 200 }
      }),
    enabled: Boolean(id) && (tab === 'roster' || tab === 'overview')
  });
  const roster = rosterQ.data?.items ?? [];
  const activeGuards = roster.filter((g) => g.active).length;

  // 3. Invoices
  const invoicesQ = useQuery({
    queryKey: ['agency-invoices', id],
    queryFn: () =>
      get<{ items: AgencyInvoice[]; next_cursor?: string | null }>(`/agencies/${id}/invoices`, {
        params: { limit: 200 }
      }),
    enabled: Boolean(id) && tab === 'invoices'
  });
  const invoices = invoicesQ.data?.items ?? [];

  // Generate-invoice dialog state
  const [genOpen, setGenOpen] = useState(false);
  const genForm = useForm<InvoiceCreateValues>({
    resolver: zodResolver(InvoiceCreateSchema),
    mode: 'onChange',
    defaultValues: {
      period_month: new Date().getMonth() + 1,
      period_year: new Date().getFullYear(),
      active_guards: 0,
      amount_inr: 0,
      notes: ''
    }
  });

  // Auto-fill active_guards + amount when agency / month changes
  const watchMonth = genForm.watch('period_month');
  const watchYear = genForm.watch('period_year');
  useEffect(() => {
    if (!genOpen || !agency) return;
    const count = activeGuards;
    const amt =
      agency.billing_model === 'flat'
        ? agency.monthly_flat_fee_inr ?? 0
        : (agency.monthly_rate_per_guard_inr ?? 0) * count;
    genForm.setValue('active_guards', count);
    genForm.setValue('amount_inr', amt);
  }, [genOpen, agency, activeGuards, watchMonth, watchYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const openGenerate = (): void => {
    const now = new Date();
    genForm.reset({
      period_month: now.getMonth() + 1,
      period_year: now.getFullYear(),
      active_guards: activeGuards,
      amount_inr:
        agency?.billing_model === 'flat'
          ? agency.monthly_flat_fee_inr ?? 0
          : (agency?.monthly_rate_per_guard_inr ?? 0) * activeGuards,
      notes: ''
    });
    setGenOpen(true);
  };

  const generateInvoice = useMutation({
    mutationFn: (v: InvoiceCreateValues) =>
      post<AgencyInvoice>(`/agencies/${id}/invoices`, {
        period_month: v.period_month,
        period_year: v.period_year,
        active_guards: v.active_guards,
        amount_inr: v.amount_inr,
        notes: v.notes?.trim() || undefined
      }),
    onSuccess: () => {
      toast.success('Invoice generated');
      setGenOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['agency-invoices', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // Edit-draft invoice
  const [editingInvoice, setEditingInvoice] = useState<AgencyInvoice | null>(null);
  const editForm = useForm<InvoiceEditValues>({
    resolver: zodResolver(InvoiceEditSchema),
    mode: 'onChange',
    defaultValues: { active_guards: 0, amount_inr: 0, notes: '' }
  });
  useEffect(() => {
    if (editingInvoice) {
      editForm.reset({
        active_guards: editingInvoice.active_guards,
        amount_inr: editingInvoice.amount_inr,
        notes: editingInvoice.notes ?? ''
      });
    }
  }, [editingInvoice]); // eslint-disable-line react-hooks/exhaustive-deps
  const editInvoice = useMutation({
    mutationFn: (v: InvoiceEditValues) => {
      if (!editingInvoice) throw new Error('No invoice');
      return patch<AgencyInvoice>(`/agency-invoices/${editingInvoice.id}`, {
        active_guards: v.active_guards,
        amount_inr: v.amount_inr,
        notes: v.notes?.trim() || undefined
      });
    },
    onSuccess: () => {
      toast.success('Invoice updated');
      setEditingInvoice(null);
      void queryClient.invalidateQueries({ queryKey: ['agency-invoices', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // Mark paid
  const [payingInvoice, setPayingInvoice] = useState<AgencyInvoice | null>(null);
  const payForm = useForm<MarkPaidValues>({
    resolver: zodResolver(MarkPaidSchema),
    mode: 'onChange',
    defaultValues: { payment_ref: '' }
  });
  useEffect(() => {
    if (payingInvoice) payForm.reset({ payment_ref: '' });
  }, [payingInvoice]); // eslint-disable-line react-hooks/exhaustive-deps
  const markPaid = useMutation({
    mutationFn: (v: MarkPaidValues) => {
      if (!payingInvoice) throw new Error('No invoice');
      return post<AgencyInvoice>(`/agency-invoices/${payingInvoice.id}/mark-paid`, {
        payment_ref: v.payment_ref
      });
    },
    onSuccess: () => {
      toast.success('Marked paid');
      setPayingInvoice(null);
      void queryClient.invalidateQueries({ queryKey: ['agency-invoices', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // Dispute
  const [disputingInvoice, setDisputingInvoice] = useState<AgencyInvoice | null>(null);
  const disputeForm = useForm<DisputeValues>({
    resolver: zodResolver(DisputeSchema),
    mode: 'onChange',
    defaultValues: { notes: '' }
  });
  useEffect(() => {
    if (disputingInvoice) disputeForm.reset({ notes: '' });
  }, [disputingInvoice]); // eslint-disable-line react-hooks/exhaustive-deps
  const disputeInvoice = useMutation({
    mutationFn: (v: DisputeValues) => {
      if (!disputingInvoice) throw new Error('No invoice');
      return post<AgencyInvoice>(`/agency-invoices/${disputingInvoice.id}/dispute`, {
        notes: v.notes
      });
    },
    onSuccess: () => {
      toast.success('Invoice disputed');
      setDisputingInvoice(null);
      void queryClient.invalidateQueries({ queryKey: ['agency-invoices', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // Deactivate / reactivate / delete on the footer card.
  const deactivate = useMutation({
    mutationFn: (aid: string) => post<SecurityAgency>(`/agencies/${aid}/deactivate`, {}),
    onSuccess: () => {
      toast.success('Agency deactivated');
      setDeactivateTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['agency-detail', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });
  const reactivate = useMutation({
    mutationFn: (aid: string) => post<SecurityAgency>(`/agencies/${aid}/reactivate`, {}),
    onSuccess: () => {
      toast.success('Agency reactivated');
      void queryClient.invalidateQueries({ queryKey: ['agency-detail', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });
  const remove = useMutation({
    mutationFn: (aid: string) => del<unknown>(`/agencies/${aid}`),
    onSuccess: () => {
      toast.success('Agency deleted');
      void navigate({ to: '/agencies' });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // KPI quick stats for the overview
  const expectedOutflow = useMemo(() => {
    if (!agency || !agency.is_active) return 0;
    if (agency.billing_model === 'flat') return agency.monthly_flat_fee_inr ?? 0;
    return (agency.monthly_rate_per_guard_inr ?? 0) * activeGuards;
  }, [agency, activeGuards]);

  // ───── loading + error ─────
  if (agencyQ.isLoading) {
    return (
      <>
        <PageHeader title="Agency" description="Loading agency profile…" />
        <div className="mx-auto w-full max-w-[1200px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
          <Card>
            <CardContent>
              <Skeleton className="h-40 w-full" />
            </CardContent>
          </Card>
        </div>
      </>
    );
  }
  if (agencyQ.isError || !agency) {
    return (
      <>
        <PageHeader title="Agency" description="Could not load this agency." />
        <div className="mx-auto w-full max-w-[1200px] px-vt-6 pb-vt-8 pt-vt-4">
          <Card>
            <CardContent>
              <EmptyState
                icon={<AlertTriangle className="h-6 w-6" />}
                title="Agency not found"
                description={
                  agencyQ.error
                    ? getErrorMessage(agencyQ.error)
                    : 'This record may have been removed or you may not have access.'
                }
                action={
                  <Button variant="secondary" onClick={() => void navigate({ to: '/agencies' })}>
                    <ArrowLeft className="h-4 w-4" /> Back to agencies
                  </Button>
                }
              />
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // ───── dialogs ─────

  const generateDialog = (
    <Dialog open={genOpen} onOpenChange={setGenOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate invoice</DialogTitle>
          <DialogDescription>
            Defaults are pre-filled from this agency's rate and current active roster — override
            either if needed.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={genForm.handleSubmit((v) => generateInvoice.mutate(v))}
          className="flex flex-col gap-vt-3"
        >
          <div className="grid grid-cols-2 gap-vt-3">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="gi-month">Month</Label>
              <Select id="gi-month" {...genForm.register('period_month')}>
                {Array.from({ length: 12 }).map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {monthLabel(i + 1, 2026).split(' ')[0]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="gi-year">Year</Label>
              <Input id="gi-year" type="number" min={2020} max={2100} {...genForm.register('period_year')} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-vt-3">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="gi-count">Active guards</Label>
              <Input id="gi-count" type="number" min={0} {...genForm.register('active_guards')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="gi-amount">Amount (₹)</Label>
              <Input id="gi-amount" type="number" min={0} {...genForm.register('amount_inr')} />
            </div>
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="gi-notes">Notes</Label>
            <Textarea id="gi-notes" rows={2} {...genForm.register('notes')} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setGenOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={generateInvoice.isPending}
              loadingText="Generating…"
              disabled={generateInvoice.isPending || !genForm.formState.isValid}
            >
              Generate invoice
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  const editDialog = (
    <Dialog
      open={Boolean(editingInvoice)}
      onOpenChange={(v) => (v ? null : setEditingInvoice(null))}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit draft invoice</DialogTitle>
          <DialogDescription>
            {editingInvoice
              ? `${monthLabel(editingInvoice.period_month, editingInvoice.period_year)} — draft only.`
              : null}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={editForm.handleSubmit((v) => editInvoice.mutate(v))}
          className="flex flex-col gap-vt-3"
        >
          <div className="grid grid-cols-2 gap-vt-3">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="ei-count">Active guards</Label>
              <Input id="ei-count" type="number" min={0} {...editForm.register('active_guards')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="ei-amount">Amount (₹)</Label>
              <Input id="ei-amount" type="number" min={0} {...editForm.register('amount_inr')} />
            </div>
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="ei-notes">Notes</Label>
            <Textarea id="ei-notes" rows={2} {...editForm.register('notes')} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setEditingInvoice(null)}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={editInvoice.isPending}
              loadingText="Saving…"
              disabled={editInvoice.isPending || !editForm.formState.isValid}
            >
              Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  const markPaidDialog = (
    <Dialog
      open={Boolean(payingInvoice)}
      onOpenChange={(v) => (v ? null : setPayingInvoice(null))}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark invoice as paid</DialogTitle>
          <DialogDescription>
            {payingInvoice
              ? `${monthLabel(payingInvoice.period_month, payingInvoice.period_year)} — ${formatInr(payingInvoice.amount_inr)}`
              : null}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={payForm.handleSubmit((v) => markPaid.mutate(v))}
          className="flex flex-col gap-vt-3"
        >
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="mp-ref">Payment reference</Label>
            <Input id="mp-ref" placeholder="UTR / cheque #" {...payForm.register('payment_ref')} />
            {payForm.formState.errors.payment_ref ? (
              <p className="text-vt-12 text-vt-alert">{payForm.formState.errors.payment_ref.message}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setPayingInvoice(null)}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={markPaid.isPending}
              loadingText="Saving…"
              disabled={markPaid.isPending || !payForm.formState.isValid}
            >
              Mark paid
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  const disputeDialog = (
    <Dialog
      open={Boolean(disputingInvoice)}
      onOpenChange={(v) => (v ? null : setDisputingInvoice(null))}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Dispute invoice</DialogTitle>
          <DialogDescription>
            Flag this invoice so it stays out of paid totals until reconciled with the agency.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={disputeForm.handleSubmit((v) => disputeInvoice.mutate(v))}
          className="flex flex-col gap-vt-3"
        >
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="dp-notes">Reason</Label>
            <Textarea id="dp-notes" rows={3} placeholder="e.g. Roster count overstated by 2." {...disputeForm.register('notes')} />
            {disputeForm.formState.errors.notes ? (
              <p className="text-vt-12 text-vt-alert">{disputeForm.formState.errors.notes.message}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setDisputingInvoice(null)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="danger"
              loading={disputeInvoice.isPending}
              loadingText="Saving…"
              disabled={disputeInvoice.isPending || !disputeForm.formState.isValid}
            >
              Dispute
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  // ───── render ─────
  return (
    <>
      <PageHeader
        title={agency.name}
        description={`Security agency${agency.registration_no ? ` · ${agency.registration_no}` : ''}`}
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/agencies' })} title="Back to agencies">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        }
      />

      <div className="mx-auto w-full max-w-[1200px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* Header card */}
        <Card>
          <CardContent className="flex flex-col gap-vt-4 md:flex-row md:items-start p-vt-5">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-vt-md bg-vt-primary-100 text-vt-primary">
              <Building2 className="h-7 w-7" strokeWidth={1.75} />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-vt-24 font-semibold tracking-[-0.01em] text-vt-text">
                {agency.name}
              </h2>
              <div className="mt-vt-1 flex flex-wrap items-center gap-vt-3 text-vt-13 text-vt-text-muted">
                {agency.registration_no ? (
                  <span className="font-mono">{agency.registration_no}</span>
                ) : null}
                {agency.contact_phone ? (
                  <span className="inline-flex items-center gap-1">
                    <Phone className="h-3.5 w-3.5" strokeWidth={1.75} /> {agency.contact_phone}
                  </span>
                ) : null}
                {agency.contact_email ? (
                  <span className="inline-flex items-center gap-1">
                    <Mail className="h-3.5 w-3.5" strokeWidth={1.75} /> {agency.contact_email}
                  </span>
                ) : null}
                {agency.contract_start || agency.contract_end ? (
                  <span className="inline-flex items-center gap-1">
                    <CalendarDays className="h-3.5 w-3.5" strokeWidth={1.75} />
                    {agency.contract_start ? formatDate(agency.contract_start) : '—'} →{' '}
                    {agency.contract_end ? formatDate(agency.contract_end) : '—'}
                  </span>
                ) : null}
              </div>
              <div className="mt-vt-2 flex flex-wrap items-center gap-vt-2">
                {agency.is_active ? (
                  <Badge variant="ok">Active</Badge>
                ) : (
                  <Badge variant="muted">Inactive</Badge>
                )}
                <Badge variant="info">
                  {agency.billing_model === 'flat' ? 'Flat fee' : 'Per guard'}
                </Badge>
              </div>
            </div>
            <div className="shrink-0">
              <Button variant="secondary" size="sm" asChild>
                <Link to="/agencies" search={{ edit: agency.id } as never}>
                  <Edit3 className="h-4 w-4" /> Edit
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card>
          <CardContent className="flex items-center gap-vt-2 p-vt-3">
            <TabsPills
              value={tab}
              onChange={(v) => setTab(v as Tab)}
              options={[
                { value: 'overview', label: 'Overview' },
                { value: 'roster', label: 'Roster', count: roster.length },
                { value: 'invoices', label: 'Invoices', count: invoices.length || undefined }
              ]}
              aria-label="Agency sections"
            />
          </CardContent>
        </Card>

        {tab === 'overview' ? (
          <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-2 xl:grid-cols-3">
            <Card>
              <CardContent className="p-vt-5">
                <div className="text-vt-12 uppercase tracking-wide text-vt-text-soft">Billing model</div>
                <div className="mt-vt-1 text-vt-18 font-semibold text-vt-text">
                  {agency.billing_model === 'flat' ? 'Flat monthly fee' : 'Per guard'}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-vt-5">
                <div className="text-vt-12 uppercase tracking-wide text-vt-text-soft">
                  {agency.billing_model === 'flat' ? 'Monthly flat fee' : 'Rate per guard'}
                </div>
                <div className="mt-vt-1 text-vt-18 font-semibold text-vt-text">
                  {agency.billing_model === 'flat'
                    ? formatInr(agency.monthly_flat_fee_inr)
                    : formatInr(agency.monthly_rate_per_guard_inr)}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-vt-5">
                <div className="text-vt-12 uppercase tracking-wide text-vt-text-soft">Contracted</div>
                <div className="mt-vt-1 text-vt-18 font-semibold text-vt-text">
                  {agency.contracted_guard_count ?? '—'}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-vt-5">
                <div className="text-vt-12 uppercase tracking-wide text-vt-text-soft">Active guards now</div>
                <div className="mt-vt-1 text-vt-18 font-semibold text-vt-text">
                  {rosterQ.isLoading ? <Skeleton className="h-6 w-12" /> : activeGuards}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-vt-5">
                <div className="text-vt-12 uppercase tracking-wide text-vt-text-soft">
                  Expected monthly outflow
                </div>
                <div className="mt-vt-1 text-vt-18 font-semibold text-vt-text">
                  {formatInr(expectedOutflow)}
                </div>
              </CardContent>
            </Card>
            <Card className="md:col-span-2 xl:col-span-3">
              <CardContent className="p-vt-5">
                <div className="text-vt-12 uppercase tracking-wide text-vt-text-soft">Notes</div>
                <p className="mt-vt-1 whitespace-pre-wrap text-vt-13 text-vt-text">
                  {agency.notes ? agency.notes : '—'}
                </p>
              </CardContent>
            </Card>
          </div>
        ) : null}

        {tab === 'roster' ? (
          <Card>
            <CardContent className="p-vt-4">
              {rosterQ.isLoading ? (
                <div className="flex flex-col gap-vt-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : rosterQ.isError ? (
                <EmptyState
                  icon={<Users className="h-6 w-6" />}
                  title="Could not load roster"
                  description={getErrorMessage(rosterQ.error)}
                />
              ) : roster.length === 0 ? (
                <EmptyState
                  icon={<Users className="h-6 w-6" />}
                  title="No guards yet"
                  description="Once guards are added with this agency assigned, they will appear here."
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Guard</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Agency employee #</TableHead>
                      <TableHead>Hire date</TableHead>
                      <TableHead>Contract until</TableHead>
                      <TableHead>Posted at</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {roster.map((g) => (
                      <TableRow key={g.id}>
                        <TableCell>
                          <Link
                            to="/guards/$id"
                            params={{ id: g.id }}
                            className="font-medium text-vt-text hover:text-vt-primary hover:underline"
                          >
                            {g.full_name}
                          </Link>
                        </TableCell>
                        <TableCell className="text-vt-12 text-vt-text-muted">{g.phone}</TableCell>
                        <TableCell className="font-mono text-vt-12 text-vt-text-muted">
                          {g.agency_employee_id ?? '—'}
                        </TableCell>
                        <TableCell className="text-vt-12 text-vt-text-muted">
                          {g.hire_date ? formatDate(g.hire_date) : '—'}
                        </TableCell>
                        <TableCell className="text-vt-12 text-vt-text-muted">
                          {g.contract_until ? formatDate(g.contract_until) : '—'}
                        </TableCell>
                        <TableCell className="text-vt-12 text-vt-text">
                          {g.duty_kind === 'gate' && g.gate ? `Gate · ${g.gate.name}` :
                            g.duty_kind === 'tower' && g.tower ? `Tower · ${g.tower.name}` :
                            g.duty_kind ?? '—'}
                        </TableCell>
                        <TableCell>
                          {g.active ? <Badge variant="ok">Active</Badge> : <Badge variant="muted">Inactive</Badge>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        ) : null}

        {tab === 'invoices' ? (
          <Card>
            <CardContent className="p-vt-4">
              <div className="mb-vt-3 flex items-center justify-between">
                <div className="text-vt-13 text-vt-text-muted">
                  Monthly invoices generated for this agency.
                </div>
                <Button size="sm" onClick={openGenerate}>
                  <Plus className="h-4 w-4" /> Generate invoice
                </Button>
              </div>
              {invoicesQ.isLoading ? (
                <div className="flex flex-col gap-vt-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : invoicesQ.isError ? (
                <EmptyState
                  icon={<Receipt className="h-6 w-6" />}
                  title="Could not load invoices"
                  description={getErrorMessage(invoicesQ.error)}
                />
              ) : invoices.length === 0 ? (
                <EmptyState
                  icon={<Receipt className="h-6 w-6" />}
                  title="No invoices yet"
                  description="Generate the first monthly invoice to start tracking payouts."
                  action={
                    <Button onClick={openGenerate}>
                      <Plus className="h-4 w-4" /> Generate invoice
                    </Button>
                  }
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Period</TableHead>
                      <TableHead className="text-right">Guards</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Paid at</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((inv) => (
                      <TableRow key={inv.id}>
                        <TableCell className="font-medium text-vt-text">
                          {monthLabel(inv.period_month, inv.period_year)}
                        </TableCell>
                        <TableCell className="text-right text-vt-text-muted">
                          {inv.active_guards}
                        </TableCell>
                        <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                          {formatInr(inv.amount_inr)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={STATUS_VARIANT[inv.status]}>{STATUS_LABEL[inv.status]}</Badge>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-vt-12 text-vt-text-muted">
                          {inv.paid_at ? formatDateTime(inv.paid_at) : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex items-center gap-vt-1">
                            {inv.status === 'draft' ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Edit draft"
                                onClick={() => setEditingInvoice(inv)}
                              >
                                <Edit3 className="h-4 w-4" />
                              </Button>
                            ) : null}
                            {inv.status !== 'paid' ? (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => setPayingInvoice(inv)}
                              >
                                <CheckCircle2 className="h-4 w-4" /> Mark paid
                              </Button>
                            ) : null}
                            {inv.status !== 'paid' && inv.status !== 'disputed' ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Dispute"
                                onClick={() => setDisputingInvoice(inv)}
                              >
                                <AlertTriangle className="h-4 w-4" />
                              </Button>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        ) : null}

        {/* Footer — destructive actions */}
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-vt-3 p-vt-5">
            <div className="text-vt-13 text-vt-text-muted">
              {agency.is_active
                ? 'Deactivate to retain history without showing this agency in active workflows.'
                : 'This agency is currently inactive.'}
            </div>
            <div className="flex items-center gap-vt-2">
              {agency.is_active ? (
                <Button variant="danger" size="sm" onClick={() => setDeactivateTarget(agency)}>
                  <Power className="h-4 w-4" /> Deactivate
                </Button>
              ) : (
                <Button variant="secondary" size="sm" onClick={() => reactivate.mutate(agency.id)}>
                  <RotateCcw className="h-4 w-4" /> Reactivate
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(agency)}>
                Delete…
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {generateDialog}
      {editDialog}
      {markPaidDialog}
      {disputeDialog}

      <ConfirmDialog
        open={Boolean(deactivateTarget)}
        onOpenChange={(v) => (v ? null : setDeactivateTarget(null))}
        title="Deactivate agency"
        description={
          deactivateTarget ? (
            <>
              Deactivate <strong>{deactivateTarget.name}</strong>? Guards on this roster stay on
              file, but no new invoices can be generated until the agency is reactivated.
            </>
          ) : null
        }
        confirmLabel="Deactivate"
        variant="danger"
        loading={deactivate.isPending}
        onConfirm={() => {
          if (!deactivateTarget) return;
          deactivate.mutate(deactivateTarget.id);
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(v) => (v ? null : setDeleteTarget(null))}
        title="Delete agency"
        description={
          deleteTarget ? (
            <>
              Permanently delete <strong>{deleteTarget.name}</strong>? Only allowed when no
              guards or invoices reference this agency.
            </>
          ) : null
        }
        confirmLabel="Delete"
        variant="danger"
        typeToConfirm="DELETE"
        loading={remove.isPending}
        onConfirm={() => {
          if (!deleteTarget) return;
          remove.mutate(deleteTarget.id);
        }}
      />
    </>
  );
}
